import { z } from 'zod';

import { activityDimensions, activityPosture } from './activity-dimensions';
import { cameraVisionSettings, cameraZone, cameraZoneType, visionModelSettings } from './cameras';

export const activityLighting = z.enum(['day', 'ir']);
export type ActivityLighting = z.infer<typeof activityLighting>;

export const activityInferenceRequest = z.object({
  requestId: z.string().min(1),
  streamId: z.string().min(1),
  cameraId: z.string().min(1),
  clip: z.object({ bucket: z.string().min(1), key: z.string().min(1) }),
  lightingHint: activityLighting.nullable().optional(),
  zones: z.array(cameraZone).max(40),
  sampleFps: z.number().positive().max(10).optional(),
  settings: visionModelSettings.optional(),
});
export type ActivityInferenceRequest = z.infer<typeof activityInferenceRequest>;

export const activityInferenceResponse = z.object({
  requestId: z.string(),
  modelVersions: z.object({ detector: z.string(), pose: z.string(), activity: z.string() }),
  lighting: activityLighting,
  framesSampled: z.number().int().nonnegative(),
  latencyMs: z.number().int().nonnegative(),
  people: z.array(
    z.object({
      trackId: z.number().int(),
      patientScore: z.number(),
      posture: activityPosture,
      zoneType: cameraZoneType.nullable(),
      presenceRatio: z.number(),
    }),
  ),
  patient: z.object({ trackId: z.number().int().nullable(), confidence: z.number().min(0).max(1) }),
  dimensions: activityDimensions,
});
export type ActivityInferenceResponse = z.infer<typeof activityInferenceResponse>;

export const liveVisionStage = z.enum(['raw', 'preprocessed', 'detection', 'pose', 'result']);
export type LiveVisionStage = z.infer<typeof liveVisionStage>;

export const visionStageView = z.object({
  width: z.number().int(),
  height: z.number().int(),
  image: z.string(),
  latencyMs: z.number().int(),
  lighting: activityLighting,
  objects: z.array(
    z.object({
      label: z.string(),
      confidence: z.number(),
      box: z.array(z.number()).length(4),
      trackId: z.number().int().nullable(),
    }),
  ),
  people: z.array(
    z.object({
      trackId: z.number().int().nullable(),
      confidence: z.number(),
      visibleKeypoints: z.number().int(),
      posture: activityPosture,
      zoneType: cameraZoneType.nullable(),
      patientScore: z.number().nullable(),
    }),
  ),
  motion: z.array(z.array(z.number()).length(4)),
  patientTrackId: z.number().int().nullable(),
});
export type VisionStageView = z.infer<typeof visionStageView>;

export const startVisionReplayBody = z.object({
  sampleFps: z.number().min(0.5).max(5),
  tracking: z.boolean(),
  settings: cameraVisionSettings.optional(),
});
export type StartVisionReplayBody = z.infer<typeof startVisionReplayBody>;

export const replayInferenceRequest = z.object({
  cameraId: z.string().min(1),
  clip: z.object({ bucket: z.string().min(1), key: z.string().min(1) }),
  zones: z.array(cameraZone).max(40),
  settings: visionModelSettings,
  sampleFps: z.number().min(0.5).max(5),
  tracking: z.boolean(),
});
export type ReplayInferenceRequest = z.infer<typeof replayInferenceRequest>;

export const visionReplayFrame = z.object({
  frameIndex: z.number().int(),
  timestampMs: z.number().int(),
  stages: z.record(liveVisionStage, visionStageView),
});
export type VisionReplayFrame = z.infer<typeof visionReplayFrame>;

export const activityCaptureIdParam = z.object({ captureId: z.uuid() });
export type ActivityCaptureIdParam = z.infer<typeof activityCaptureIdParam>;

export const VISION_SNIPPET_MIN_SECONDS = 5;
export const VISION_SNIPPET_MAX_SECONDS = 10;

export const recordVisionSnippetBody = z.object({
  durationSeconds: z.number().int().min(VISION_SNIPPET_MIN_SECONDS).max(VISION_SNIPPET_MAX_SECONDS),
});
export type RecordVisionSnippetBody = z.infer<typeof recordVisionSnippetBody>;

export const visionSnippet = z.object({
  id: z.string(),
  cameraId: z.string(),
  durationMs: z.number().int(),
  source: z.string(),
  createdAt: z.string(),
  url: z.string().nullable(),
});
export type VisionSnippet = z.infer<typeof visionSnippet>;

export const visionSnippetIdParam = z.object({ snippetId: z.uuid() });
export type VisionSnippetIdParam = z.infer<typeof visionSnippetIdParam>;
